import { useEffect, useState } from "react";
import axios from "axios";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

export const PricingTab = () => {
  const { settings, refreshSettings } = useSiteData();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form)
      setForm({
        currency: settings.currency,
        half_day_price: settings.half_day_price,
        full_day_price: settings.full_day_price,
        promo_enabled: settings.promo_enabled,
        promo_text: settings.promo_text,
        whatsapp: settings.whatsapp,
      });
  }, [settings, form]);

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.put(`${API}/settings`, form, { withCredentials: true });
      await refreshSettings();
      toast.success("Pricing & promotion settings saved.");
    } catch {
      toast.error("Could not save the settings.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl space-y-6" data-testid="admin-pricing-form">
      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 space-y-5 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
        <h3 className="font-display font-bold text-lg text-[#0b132b]">Currency</h3>
        <p className="text-sm text-slate-500 -mt-3">Shown next to every price on the site. Change tour prices per tour in the Tours tab.</p>
        <label className="block max-w-xs">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Currency</span>
          <select data-testid="pricing-currency-select" value={form.currency} onChange={set("currency")} className={`${inputCls} mt-1.5`}>
            {["CHF", "EUR", "USD", "GBP"].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">WhatsApp number (for booking contact buttons)</span>
          <input data-testid="pricing-whatsapp-input" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+41 79 123 45 67" className={`${inputCls} mt-1.5`} />
        </label>
      </div>

      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 space-y-5 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
        <h3 className="font-display font-bold text-lg text-[#0b132b]">Tour Duration Prices</h3>
        <p className="text-sm text-slate-500 -mt-3">The two flat rates guests pick between in the booking form. Vehicle cards show no prices — the duration package is the price.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Half Day price</span>
            <input data-testid="pricing-half-day-input" type="number" min="0" step="10" value={form.half_day_price} onChange={set("half_day_price")} className={`${inputCls} mt-1.5`} />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Full Day price</span>
            <input data-testid="pricing-full-day-input" type="number" min="0" step="10" value={form.full_day_price} onChange={set("full_day_price")} className={`${inputCls} mt-1.5`} />
          </label>
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 space-y-5 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
        <h3 className="font-display font-bold text-lg text-[#0b132b]">Promotional Banner</h3>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="pricing-promo-toggle">
          <input
            type="checkbox"
            checked={form.promo_enabled}
            onChange={(e) => setForm({ ...form, promo_enabled: e.target.checked })}
            className="w-5 h-5 rounded accent-[var(--brand)]"
            data-testid="pricing-promo-checkbox"
          />
          <span className="text-sm font-bold text-[#0b132b]">Show a promotional banner at the top of the website</span>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Banner text</span>
          <input
            data-testid="pricing-promo-text"
            value={form.promo_text}
            onChange={set("promo_text")}
            placeholder="e.g. Summer Special — 15% off all Alpine tours this week!"
            className={`${inputCls} mt-1.5`}
          />
        </label>
        <p className="text-xs text-slate-400">Tip: set a sale price on any tour in the Tours tab to show a discounted price with the old price crossed out.</p>
      </div>

      <button
        data-testid="pricing-save-button"
        type="submit"
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
      >
        <Save className="w-4 h-4" />
        {busy ? "Saving…" : "Save Settings"}
      </button>
    </form>
  );
};
